import styled, { css } from 'styled-components';

import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import {
  scale070,
  scale080,
  scale090,
  scale100,
  scale110,
  scale130,
  spaceL,
  spaceM,
  xdlColorText,
} from '@xing-com/tokens';

import type { HeadlineSize } from './';

type HeadlineTProps = {
  $ellipsis?: boolean;
  $noMargin?: boolean;
  $size: HeadlineSize;
  $sizeConfined?: HeadlineSize;
  $sizeWide?: HeadlineSize;
};

// *** Base ***
const baseHeadline = css`
  color: ${xdlColorText};
  font-weight: 700;
  line-height: 1.25;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  padding: 0;
`;

const noMargin = css`
  && {
    margin: 0;
  }
`;

const ellipsis = css`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// *** Sizes ***
export const sizes = {
  small: css`
    font-size: ${scale070};
    margin-bottom: ${spaceM};
  `,
  medium: css`
    font-size: ${scale080};
    margin-bottom: ${spaceM};
  `,
  large: css`
    font-size: ${scale090};
    margin-bottom: ${spaceL};
  `,
  xlarge: css`
    font-size: ${scale100};
    margin-bottom: ${spaceL};
  `,
  xxlarge: css`
    font-size: ${scale110};
    margin-bottom: ${spaceL};
  `,
  xxxlarge: css`
    font-size: ${scale130};
    margin-bottom: ${spaceL};
  `,
};

// *** Components ***
const Headline = styled.h2<HeadlineTProps>`
  ${baseHeadline};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      case 'large':
        return sizes.large;
      case 'xlarge':
        return sizes.xlarge;
      case 'xxlarge':
        return sizes.xxlarge;
      case 'xxxlarge':
        return sizes.xxxlarge;
      default:
        return $size satisfies undefined;
    }
  }}

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xxxlarge}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xlarge}
          }
        `;
      case 'xxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxlarge}
          }
        `;
      case 'xxxlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xxxlarge}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $noMargin }) => $noMargin && noMargin};
  ${({ $ellipsis }) => $ellipsis && ellipsis};
`;

export const Styled = {
  Headline,
};
